"use client";
import useTranslate from "@app/_hooks/useTranslate";

import Link from "@components/Link";
import { Accordion, Button } from "@components/ngg";
import { useCartActions, useCartState } from "@context/cartContext";
import Image from "next/image";
import type { MouseEvent } from "react";
import { useMemo, useState } from "react";
import type { Variant } from "types/product";

import { cn } from "@/lib/utils";
import { formatPrice } from "@/utils/priceUtils";
// import { isMadeToOrderItem } from '@/utils/product/isMadeToOrderItem';
// import { isSoonBackInStock } from '@/utils/product/isSoonBackInStock';

import InStockItem from "@app/_components/Stock/statuses/InStockItem";
import { StockStatusEnum } from "@app/_components/Stock/Stock.types";
import Icon from "./icon";

function LineItem({ item, locale }: { item: Variant; locale?: string }) {
  const t = useTranslate();
  const { cart } = useCartState();
  const { addCartItem } = useCartActions();
  const [state, setState] = useState<string>("idle");

  const inCart = useMemo(
    () => cart?.items.some((cartItem) => cartItem.partNo === item.id),
    [cart, item],
  );

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (inCart || state === "added") return;
    setState("loading");
    await addCartItem(
      [
        {
          item,
          quantity: 1,
        },
      ],
      true,
    );
    setState("added");
  };

  return (
    <li key={item.id} className="relative">
      <Link href={item.link} locale={locale} className="flex">
        {item.image && (
          <div className="block min-h-24 w-24 shrink-0 bg-gray-100">
            <Image
              className="h-full w-full overflow-hidden object-contain p-1 mix-blend-multiply"
              src={item.image.src}
              alt={item.image.alt}
              height={100}
              width={100}
            />
          </div>
        )}
        <div className="flex w-full flex-col gap-1 px-4 py-3 text-base">
          <span className="block w-full truncate overflow-hidden">
            {item.title}
          </span>
          <span className="block w-full truncate overflow-hidden">
            {formatPrice(item.price, locale)}
          </span>
          <div className="flex w-full items-center justify-between gap-1 max-lg:flex-col">
            <InStockItem
              config={{ type: "plp", item: item, className: "uppercase" }}
              status={{
                code: StockStatusEnum.IN_STOCK_ITEM,
                quantity: 1,
                pretty: "1",
              }}
            />
            <Button
              onClick={handleClick}
              isLoading={state === "loading"}
              className={cn(
                "justify-self-end",
                "h-auto self-end px-3 py-1 text-sm whitespace-nowrap",
                !inCart && state === "idle" ? "btn-outlined" : "btn-primary",
              )}
            >
              {inCart || state === "added" ? t("added") : t("add")}
            </Button>
          </div>
        </div>
      </Link>
    </li>
  );
}

export default function Accessories({
  accessories,
  locale,
  className,
}: {
  accessories: Variant[];
  locale?: string;
  className?: string;
}) {
  const t = useTranslate();
  return (
    <Accordion
      IconComponent={Icon}
      WrapperComponent="div"
      title={t("recommendedAccessories")}
      classNames={{
        wrapper: className,
        button: "text-left items-center leading-4 p-4 font-normal rounded-none",
        panel: "p-4",
      }}
      defaultOpen
      transitionProps={{ appear: false }}
    >
      <ul className="flex flex-col gap-2">
        {accessories.map((item) => (
          <LineItem key={item.id} item={item} locale={locale} />
        ))}
      </ul>
    </Accordion>
  );
}
